import React, { useState, useEffect, useMemo } from 'react';
import { BottomSheet } from '../../../shared/ui';
import { SessionV2 } from '../../../api/sessionsV2Api';
import { sitePaymentAllocationV2Api, BonusPreviewResponseV2 } from '../../../api/sitePaymentAllocationV2Api';
import { useToast } from '../../../shared/contexts/ToastContext';
import './BonusPaymentBottomSheet.css';

interface BonusPaymentBottomSheetProps {
  isOpen: boolean;
  sessions: SessionV2[];
  siteId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 }).format(value);
const formatHours = (h: number) => (h.toFixed(2) + ' ч').replace('.', ',');
/** Компактно: ставка в ₽/ч без слова «руб.» */
const formatRubPerHour = (value: number) =>
  `${new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} ₽/ч`;

const BonusPaymentBottomSheet: React.FC<BonusPaymentBottomSheetProps> = ({
  isOpen,
  sessions,
  siteId,
  onClose,
  onSuccess
}) => {
  const [preview, setPreview] = useState<BonusPreviewResponseV2 | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** Ставка ₽/ч по виду работы (строка из инпута) */
  const [rateByTypeId, setRateByTypeId] = useState<Record<number, string>>({});
  const { showError, showSuccess } = useToast();

  const firstSession = sessions[0];
  const siteExecutorId =
    firstSession?.siteExecutorId && firstSession.siteExecutorId > 0 ? firstSession.siteExecutorId : undefined;
  const userId = siteExecutorId ? undefined : (firstSession?.userId ?? undefined);
  const sessionIds = sessions.map(s => s.id);

  useEffect(() => {
    if (!isOpen || sessions.length === 0 || sessionIds.length === 0) {
      setPreview(null);
      setRateByTypeId({});
      setLoading(false);
      return;
    }
    setLoading(true);
    sitePaymentAllocationV2Api
      .getBonusPreview(siteId, { sessionIds })
      .then((p) => {
        setPreview(p);
        const next: Record<number, string> = {};
        (p.byTypeOfWork ?? []).forEach((row) => {
          next[row.typeOfWorkId] = '';
        });
        setRateByTypeId(next);
      })
      .catch((err: any) => {
        showError(err.response?.data ?? 'Ошибка загрузки превью премии');
        setPreview(null);
      })
      .finally(() => setLoading(false));
  }, [isOpen, siteId, sessionIds.join(',')]);

  const linesTotal = useMemo(() => {
    if (!preview?.byTypeOfWork?.length) return 0;
    let sum = 0;
    for (const row of preview.byTypeOfWork) {
      const raw = (rateByTypeId[row.typeOfWorkId] ?? '').replace(',', '.').trim();
      const perHour = parseFloat(raw);
      if (!Number.isFinite(perHour) || perHour <= 0) continue;
      sum += row.hours * perHour;
    }
    return sum;
  }, [preview, rateByTypeId]);

  const setRate = (typeOfWorkId: number, value: string) => {
    setRateByTypeId((prev) => ({ ...prev, [typeOfWorkId]: value }));
  };

  const handleAccrue = async () => {
    if (!preview || preview.byTypeOfWork.length === 0) {
      showError('Нет видов работ для начисления');
      return;
    }
    const rates = preview.byTypeOfWork
      .map((row) => {
        const raw = (rateByTypeId[row.typeOfWorkId] ?? '').replace(',', '.').trim();
        const perHour = parseFloat(raw);
        return { typeOfWorkId: row.typeOfWorkId, bonusPerHour: Number.isFinite(perHour) ? perHour : 0 };
      })
      .filter((r) => r.bonusPerHour > 0.01);

    if (rates.length === 0) {
      showError('Укажите премию за час хотя бы для одного вида работ');
      return;
    }

    setSubmitting(true);
    try {
      const result = await sitePaymentAllocationV2Api.createHourlyBonusEarnings(siteId, {
        sessionIds,
        ...(siteExecutorId != null ? { siteExecutorId } : {}),
        ...(userId != null ? { userId } : {}),
        ratesByTypeOfWork: rates
      });

      if (!result.success) {
        showError(result.message ?? 'Не удалось создать начисления');
        return;
      }

      showSuccess(
        result.message ??
          `Начислено: ${result.createdCount} строк на ${formatCurrency(Number(result.totalAmount ?? 0))}`
      );
      setRateByTypeId({});
      onSuccess?.();
      onClose();
    } catch (err: any) {
      showError(err.response?.data ?? 'Ошибка начисления премии');
    } finally {
      setSubmitting(false);
    }
  };

  const title = preview ? `Премия · ${preview.userName}` : 'Премия';

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={title} className="bonus-payment-sheet">
      <div className="bonus-payment-sheet__content">
        {loading && (
          <div className="bonus-payment-sheet__loading">
            <i className="ri-loader-4-line spin" />
            <span>Загрузка данных...</span>
          </div>
        )}

        {!loading && preview && (
          <>
            <p className="bonus-payment-sheet__hint">
              Укажите премию за час по каждому виду работ. На каждую активность создаётся отдельное начисление; оплата
              — позже, при распределении платежа на эти начисления.
            </p>

            <div className="bonus-payment-sheet__summary">
              <div className="bonus-payment-sheet__row">
                <span>Общее кол-во часов</span>
                <strong>{formatHours(preview.totalHours)}</strong>
              </div>
              <div className="bonus-payment-sheet__row">
                <span>Выплачено (зарплата)</span>
                <strong>{formatCurrency(Number(preview.totalPaidSalary))}</strong>
              </div>
              {(preview.totalPaidBonus ?? 0) > 0 && (
                <div className="bonus-payment-sheet__row">
                  <span>Выплачено (премии ранее)</span>
                  <strong>{formatCurrency(Number(preview.totalPaidBonus))}</strong>
                </div>
              )}
              {preview.totalHours > 0.001 && (
                <div className="bonus-payment-sheet__row bonus-payment-sheet__row--avg">
                  <span>Средний час (ЗП + выплаченные премии)</span>
                  <strong>
                    {formatRubPerHour(
                      Number(
                        preview.averageHourlySalaryPlusPaidBonus ??
                          (Number(preview.totalPaidSalary) + Number(preview.totalPaidBonus ?? 0)) /
                            preview.totalHours
                      )
                    )}
                  </strong>
                </div>
              )}
            </div>

            {preview.byTypeOfWork.length > 0 && (
              <div className="bonus-payment-sheet__types">
                <h4>Премия по видам работ</h4>
                <p className="bonus-payment-sheet__table-legend">
                  «ЗП + прем. / ч» — средняя стоимость часа по уже выплаченной зарплате и премиям; «Будет ₽/ч» — с
                  учётом вводимой добавки к часу.
                </p>
                <div className="bonus-payment-sheet__table-wrap">
                  <table className="bonus-payment-sheet__table">
                    <thead>
                      <tr>
                        <th>Вид работы</th>
                        <th>Часы</th>
                        <th>ЗП + прем. / ч</th>
                        <th>Добавка ₽/ч</th>
                        <th>Будет ₽/ч</th>
                        <th>Сумма</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.byTypeOfWork.map((row) => {
                        const raw = (rateByTypeId[row.typeOfWorkId] ?? '').replace(',', '.').trim();
                        const perHour = parseFloat(raw);
                        const basePlusPaid =
                          row.averageHourlySalaryPlusPaidBonus ??
                          (row.hours > 0
                            ? (Number(row.paidSalary) + Number(row.paidBonus ?? 0)) / row.hours
                            : 0);
                        const lineTotal =
                          Number.isFinite(perHour) && perHour > 0 ? row.hours * perHour : 0;
                        const willBe =
                          Number.isFinite(perHour) && perHour > 0 ? basePlusPaid + perHour : null;
                        return (
                          <tr key={row.typeOfWorkId}>
                            <td>{row.typeOfWorkName}</td>
                            <td>{formatHours(row.hours)}</td>
                            <td>{formatRubPerHour(basePlusPaid)}</td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="bonus-payment-sheet__input bonus-payment-sheet__input--inline"
                                placeholder="0"
                                value={rateByTypeId[row.typeOfWorkId] ?? ''}
                                onChange={(e) => setRate(row.typeOfWorkId, e.target.value)}
                              />
                            </td>
                            <td>{willBe != null ? formatRubPerHour(willBe) : '—'}</td>
                            <td>{lineTotal > 0 ? formatCurrency(lineTotal) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {linesTotal > 0 && (
              <div className="bonus-payment-sheet__total-summary">
                <div className="bonus-payment-sheet__total-row bonus-payment-sheet__total-row--total">
                  <span>Итого к начислению:</span>
                  <span>{formatCurrency(linesTotal)}</span>
                </div>
              </div>
            )}

            <div className="bonus-payment-sheet__actions">
              <button
                type="button"
                className="bonus-payment-sheet__btn bonus-payment-sheet__btn--primary"
                disabled={submitting || linesTotal <= 0}
                onClick={handleAccrue}
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line spin" />
                    Начисление...
                  </>
                ) : (
                  'Начислить премию'
                )}
              </button>
            </div>
          </>
        )}

        {!loading && !preview && sessions.length > 0 && (
          <p className="bonus-payment-sheet__empty">Нет данных по выбранным сменам.</p>
        )}
      </div>
    </BottomSheet>
  );
};

export default BonusPaymentBottomSheet;
